// Faithful port of eb-ui `packages/eds/eds-share-box/src/ShareBox.jsx`
// (eds-share-box@1.4.5 — the version social-share pinned).
// lodash `flow(map, compact)` → plain `.map().filter(Boolean)` (same result,
// no lodash dep). Heap page-area logic comes from the `legacy-eds.utils`
// bridge, which is the migrated eds-utils source (verified identical).
import React from 'react';
import { getHeapPageArea } from '@eventbrite/legacy-eds.utils/tracking';
import { getWindowObject } from '../../lib/featureDetection';
import EmailShare from './EmailShare';
import FacebookShare from './FacebookShare';
import FacebookMessengerShare from './FacebookMessengerShare';
import LinkedinShare from './LinkedinShare';
import TwitterShare from './TwitterShare';
import WhatsappShare from './WhatsappShare';
import {
    SHARE_ON_EMAIL,
    SHARE_ON_FACEBOOK,
    SHARE_ON_FACEBOOK_MESSENGER,
    SHARE_ON_LINKEDIN,
    SHARE_ON_TWITTER,
    SHARE_ON_WHATSAPP,
    DEFAULT_SHARE_TYPES,
    SHARE_TYPES,
    DEFAULT_UTM_OPTIONS,
    UTM_SHARE_TYPE_MAP,
    DATA_SHARE_METHOD_MAP,
    DATA_HEAP_ID,
} from './constants';
import type { EmailOptions, FacebookOptions, UtmOptions } from './types';

const SHARE_TYPE_COMPONENTS_MAP: Record<
    string,
    React.ComponentType<any>
> = {
    [SHARE_ON_EMAIL]: EmailShare,
    [SHARE_ON_FACEBOOK]: FacebookShare,
    [SHARE_ON_FACEBOOK_MESSENGER]: FacebookMessengerShare,
    [SHARE_ON_TWITTER]: TwitterShare,
    [SHARE_ON_LINKEDIN]: LinkedinShare,
    [SHARE_ON_WHATSAPP]: WhatsappShare,
};

export interface ShareBoxProps {
    /** Event's id */
    eventId: string;
    /** Event's name */
    eventName: string;
    /** Event's url */
    eventUrl: string;
    /** Facebook options */
    facebookOptions: FacebookOptions;
    /** Server's url */
    serverUrl: string;
    /** Share types to show. If not provided, defaults to DEFAULT_SHARE_TYPES */
    shareTypes?: string[];
    /** Twitter handle */
    twitterHandle: string;
    /** Whether we're on a mobile device */
    isMobile?: boolean;
    /** Called on click of a share option with the clicked option's id */
    onClick?: (shareType: string) => void;
    /** Customized UTM parameters passed via share */
    utmOptions?: UtmOptions;
    /** Configurable copy options for prefilling the email */
    emailOptions?: EmailOptions;
    /** Affiliate code used to resolve the heap page area */
    affCode?: string;
    /** Affiliate channel keying into the *_AFFILIATED_CODE maps */
    affiliateChannel?: string;
}

interface ShareBoxState {
    dataPageArea: string | null;
}

interface ShareComponentOptions
    extends Omit<ShareBoxProps, 'shareTypes' | 'utmOptions'> {
    staticUrl?: boolean;
}

const _getShareComponent = (
    mergedUtmOptions: UtmOptions,
    options: ShareComponentOptions,
    shareType: string,
    dataPageArea: string | null,
) => {
    let component: React.ReactElement | null = null;

    // if current share type is not contained on the wanted share types
    // we will skip it and not build it
    if (SHARE_TYPES.includes(shareType)) {
        let boundOnClick;
        const ShareComponent = SHARE_TYPE_COMPONENTS_MAP[shareType];
        const shareMethod = DATA_SHARE_METHOD_MAP[shareType];

        const { onClick, ...shareOptions } = options;

        const typeSpecificUtmOptions = {
            ...mergedUtmOptions,
            ...UTM_SHARE_TYPE_MAP[shareType],
        };

        if (onClick) {
            boundOnClick = onClick.bind(null, shareType);
        }

        if (shareType === SHARE_ON_LINKEDIN) {
            shareOptions.staticUrl = true;
        }

        component = (
            <ShareComponent
                key={shareType}
                onClick={boundOnClick}
                utmOptions={typeSpecificUtmOptions}
                dataHeapId={DATA_HEAP_ID}
                shareMethod={shareMethod}
                dataPageArea={dataPageArea}
                {...shareOptions}
            />
        );
    }

    return component;
};

const _generateShareComponents = (
    shareTypes: string[] | undefined,
    mergedUtmOptions: UtmOptions,
    options: ShareComponentOptions,
    dataPageArea: string | null,
) =>
    (shareTypes ? shareTypes : DEFAULT_SHARE_TYPES)
        .map((shareType) =>
            _getShareComponent(
                mergedUtmOptions,
                options,
                shareType,
                dataPageArea,
            ),
        )
        .filter(Boolean);

export default class ShareBox extends React.Component<
    ShareBoxProps,
    ShareBoxState
> {
    constructor(props: ShareBoxProps) {
        super(props);
        this.state = {
            dataPageArea: getWindowObject('location')?.pathname ?? null,
        };
    }

    componentDidMount() {
        // Set the state with the current page area when the component mounts
        this.setState({ dataPageArea: getHeapPageArea(this?.props?.affCode) });
    }

    render() {
        const { shareTypes, utmOptions, ...options } = this.props;

        const mergedUtmOptions = {
            ...DEFAULT_UTM_OPTIONS,
            ...utmOptions,
        };

        const shareComponents = _generateShareComponents(
            shareTypes,
            mergedUtmOptions,
            options,
            this.state.dataPageArea,
        );

        return <div data-spec="share-box-container">{shareComponents}</div>;
    }
}
